import { toast } from 'sonner';

import { AskMarcusButton } from '@/components/actions/AskMarcusButton';
import {
  buildAnnotateAction,
  buildExportAction,
  buildGenericCustomAction,
  buildImportAction,
  buildManageAction,
  buildScanActionOptions,
  buildShareAction,
  TableActionsBar,
} from '@/components/table/actions';
import { useAxios } from '@/hooks/useAxios';
import { useEntitlements } from '@/hooks/useEntitlements';
import { useScanProfiles } from '@/hooks/useScanProfiles';
import { useSummonAurelius } from '@/hooks/useSummonAurelius';
import { queryClient } from '@/queryclient';
import { getAssetScanOptions } from '@/sections/asset/config/actionOptions';
import { useAssetActionHandlers } from '@/sections/asset/hooks/actions';
import { useAuth } from '@/state/auth';
import { useGlobalState } from '@/state/global.state';
import { Asset, AssetStatus } from '@/types';

interface AssetActionsProps {
  /** Assets to perform actions on */
  assets: Asset[];
  /** Callback for successful actions (legacy, unused) */
  onSuccess?: () => void;
  /** Additional CSS classes */
  className?: string;
  /** Hide the "Actions:" label */
  hideLabel?: boolean;
  /** Handler for opening tag modal */
  onTagClick?: () => void;
  /** Handler for opening comment modal */
  onCommentClick?: () => void;
  /** Handler for opening remove tag modal */
  onRemoveTagClick?: () => void;
  /** Handler for opening import modal (always enabled) */
  onImportClick?: () => void;
  /** Handler for opening export modal (always enabled) */
  onExportClick: () => void;
  /** Hide all action buttons */
  hideActions?: boolean;
  /** Disable actions when no items selected */
  disabled?: boolean;
}

export function AssetActions({
  assets,
  className,
  hideLabel,
  onCommentClick,
  onTagClick,
  onRemoveTagClick,
  onImportClick,
  onExportClick,
  hideActions,
  disabled = false,
}: AssetActionsProps) {
  const handlers = useAssetActionHandlers();
  const {
    modal: { selectUserAgent },
  } = useGlobalState();
  const { isPraetorianUser } = useAuth();
  const { summonAurelius } = useSummonAurelius();
  const { canWriteAssets, canWriteJobs } = useEntitlements();
  const { profiles } = useScanProfiles();
  const axios = useAxios();

  const profileOptions =
    profiles.length > 0
      ? [
          { label: 'Default (Global)', value: '' },
          ...profiles.map(p => ({
            label: p.name,
            value: p.id,
          })),
        ]
      : undefined;

  const handleProfileChange = async (profileId: string) => {
    try {
      for (const asset of assets) {
        await axios.put('/asset', { key: asset.key, profile: profileId });
      }
      // Optimistically update cached asset data so the checkmark reflects
      // immediately, even if the component remounts from a table re-render
      queryClient.setQueriesData(
        {
          predicate: query => {
            const qk = query.queryKey as string[];
            return qk[0] === 'MY' && qk[1] === 'asset';
          },
        },
        (old: unknown) => {
          if (!old || typeof old !== 'object') return old;
          const data = old as { pages?: { data: Asset[] }[] };
          if (!data.pages) return old;
          return {
            ...data,
            pages: data.pages.map(page => ({
              ...page,
              data: page.data.map((a: Asset) =>
                assets.some(sel => sel.key === a.key)
                  ? { ...a, profile: profileId || undefined }
                  : a
              ),
            })),
          };
        }
      );
      toast.success(
        profileId ? 'Scan profile updated' : 'Scan profile removed'
      );
    } catch {
      toast.error('Failed to update scan profile');
    }
  };

  // Build dropdown options using option builders
  const scanOptions = getAssetScanOptions({
    assets,
    handlers,
    selectUserAgent,
    isPraetorianUser,
  });

  // Build action configurations
  const actions = [
    buildGenericCustomAction({
      render: () => (
        <AskMarcusButton
          onClick={() =>
            summonAurelius({
              type: 'asset',
              keys: assets.map(a => a.key),
              displayNames: assets.map(a => a.dns || a.name),
            })
          }
          disabled={disabled}
          data-testid="asset-ask-marcus-button"
        />
      ),
      position: 'first',
      testId: 'asset-ask-marcus-wrapper',
    }),

    ...(canWriteAssets
      ? [
          buildAnnotateAction({
            onCommentClick: onCommentClick || (() => {}),
            onTagClick: onTagClick || (() => {}),
            onRemoveTagClick: onRemoveTagClick || (() => {}),
            labels: { comment: 'Add Note' },
            testId: 'asset-annotate-dropdown',
          }),
        ]
      : []),

    ...(canWriteJobs
      ? [
          buildScanActionOptions({
            scanOptions,
            testId: 'asset-scan-dropdown',
          }),
        ]
      : []),

    buildShareAction({
      variant: 'button-only',
      onShareClick: () => handlers.handleShare(assets),
      testId: 'asset-share-button',
    }),

    ...(canWriteAssets
      ? [
          buildManageAction({
            onFreezeClick: () =>
              handlers.handleUpdateStatus(assets, AssetStatus.Frozen),
            onActivateClick: () =>
              handlers.handleUpdateStatus(assets, AssetStatus.Active),
            onDeleteClick: () =>
              handlers.handleUpdateStatus(assets, AssetStatus.Deleted),
            profileOptions,
            onProfileChangeClick: handleProfileChange,
            currentProfileId:
              assets.length === 1 ? assets[0].profile || '' : undefined,
            shortcuts: {
              freeze: ' ⌥ F',
              activate: ' ⌥ U',
              delete: ' ⌥ D',
            },
            hideActions: {},
            testId: 'asset-managed-dropdown',
          }),
        ]
      : []),

    ...(onImportClick && canWriteAssets
      ? [
          buildImportAction({
            onImportClick,
            disabled: false, // Always enabled
            testId: 'asset-import-button',
          }),
        ]
      : []),

    buildExportAction({
      onExportClick,
      disabled: disabled,
      alwaysEnabled: false,
      testId: 'asset-export-button',
    }),
  ];

  return (
    <TableActionsBar
      selectedItems={assets}
      actions={actions}
      disabled={disabled}
      className={className}
      hideLabel={hideLabel}
      hideActions={hideActions}
      testIdPrefix="asset"
    />
  );
}
